// Birthday picker — year stepper + month grid + day grid (profile spec §3.4).
//
// **This is hand-built. There is no `<input type="date">` anywhere in it, and
// that is not a style preference.** DESIGN §10 bans the native input outright
// because on iOS it renders as a spinning wheel, which breaks the HIG feel and
// cannot express the two things this picker has to: a *past* year constrained
// to a person's plausible lifetime, and the Feb-29 common-year choice.
//
// `DiaryEditor.tsx:152` and `PairInvitePage.tsx:174` do use the native input.
// Those are recorded known debt (profile spec §8 B7, deliberately not fixed
// this sprint). **Do not copy them** — that is the one thing this file exists
// to prevent.
//
// The grid itself follows the language already established by
// `components/calendar/EventEditor.tsx` (the only other hand-built calendar in
// the repo): `grid-cols-7 gap-s-1`, `rounded-xs` day cells, `tabular-nums`
// digits, `opacity-30` for days outside the current view, and the selected
// cell as a filled disc.

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  NON_LEAP_CHOICES,
  nonLeapLabel,
  type NonLeapChoice,
} from "@/lib/profile/nonLeap";
import { cn } from "@/lib/utils/cn";

const WEEKDAY_SHORT = ["一", "二", "三", "四", "五", "六", "日"] as const;

const MONTH_LABELS = [
  "1 月", "2 月", "3 月", "4 月", "5 月", "6 月",
  "7 月", "8 月", "9 月", "10 月", "11 月", "12 月",
] as const;

/** `YYYY-MM-DD`; null means "no birthday yet". */
export type BirthdayValue = string | null;

export interface BirthdayPickerProps {
  value: BirthdayValue;
  onChange: (value: BirthdayValue) => void;
  /** Called when the picked day is Feb 29 and the substitute changed. */
  onNonLeapChange?: (choice: NonLeapChoice) => void;
  nonLeapChoice?: NonLeapChoice | null;
  today?: Date;
}

const THIS_YEAR = new Date().getFullYear();
const EARLIEST_YEAR = 1900;

function isLeap(y: number): boolean {
  return y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
}

function daysInMonth(y: number, m: number): number {
  // `new Date(y, m, 0)` is the last day of month m, but it is not usable for
  // February in a common year: `new Date(2027, 1, 29)` silently becomes Mar 1,
  // which is the exact invention ADR-0050 §5 refuses.
  if (m === 2) return isLeap(y) ? 29 : 28;
  const table = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return table[m - 1] ?? 31;
}

function parseIso(value: string): { y: number; m: number; d: number } | null {
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

export function BirthdayPicker({
  value,
  onChange,
  onNonLeapChange,
  nonLeapChoice = null,
  today = new Date(),
}: BirthdayPickerProps) {
  const parsed = useMemo(() => (value ? parseIso(value) : null), [value]);

  const [year, setYear] = useState<number>(parsed?.y ?? THIS_YEAR - 25);
  const [month, setMonth] = useState<number>(parsed?.m ?? today.getMonth() + 1);
  // `1`..`12`, or `0` meaning "still on the year stepper".
  const [step, setStep] = useState<0 | 1 | 2>(parsed ? 2 : 0);
  // What the picker's internal state was seeded from. Without this the
  // component reads `value` exactly once, on mount — and every caller that
  // fetches the profile asynchronously then shows a **different** year than the
  // one just above it in the card. That is not a cosmetic mismatch: tapping
  // 下一步 in that state saves a birthday nobody entered.
  const seededFrom = useRef(value);

  useEffect(() => {
    if (value === seededFrom.current) return;
    seededFrom.current = value;
    const next = value ? parseIso(value) : null;
    if (next) {
      setYear(next.y);
      setMonth(next.m);
      setStep(2);
    } else {
      setStep(0);
    }
  }, [value]);

  const total = daysInMonth(year, month);
  const isFeb29 = month === 2 && total === 29;

  // Monday-first offset for the 1st of the month.
  const leadingBlanks = useMemo(() => {
    const jsDay = new Date(year, month - 1, 1).getDay(); // 0 = Sunday
    return (jsDay + 6) % 7;
  }, [year, month]);

  const selected = parsed && parsed.y === year && parsed.m === month ? parsed.d : null;

  function pickDay(d: number) {
    onChange(`${year}-${pad(month)}-${pad(d)}`);
  }

  function shiftYear(delta: number) {
    setYear((y) => Math.min(THIS_YEAR, Math.max(EARLIEST_YEAR, y + delta)));
  }

  return (
    <div className="flex flex-col gap-s-4" data-testid="birthday-picker">
      {/* --- step 1: year ---------------------------------------------------- */}
      {step === 0 ? (
        <div className="flex flex-col items-center gap-s-4" data-testid="birthday-step-year">
          <p className="text-meta text-ink-3">出生年份</p>
          <div className="flex items-center gap-s-3">
            <button
              type="button"
              onClick={() => shiftYear(-1)}
              disabled={year <= EARLIEST_YEAR}
              aria-label="上一年"
              data-testid="birthday-year-prev"
              className="tap-target press h-11 w-11 flex items-center justify-center rounded-pill border border-hairline text-ink-2 hover:bg-black/5 disabled:opacity-30"
            >
              <ChevronLeft size={20} strokeWidth={1.5} aria-hidden="true" />
            </button>
            <span
              className="font-display text-display-2 text-ink-1 tabular-nums"
              data-testid="birthday-year-value"
              aria-live="polite"
            >
              {year}
            </span>
            <button
              type="button"
              onClick={() => shiftYear(1)}
              disabled={year >= THIS_YEAR}
              aria-label="下一年"
              data-testid="birthday-year-next"
              className="tap-target press h-11 w-11 flex items-center justify-center rounded-pill border border-hairline text-ink-2 hover:bg-black/5 disabled:opacity-30"
            >
              <ChevronRight size={20} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setStep(1)}
            data-testid="birthday-year-continue"
            className="tap-target press min-h-11 rounded-pill bg-ink-1 px-s-6 text-body-sm font-semibold text-paper"
          >
            下一步
          </button>
        </div>
      ) : null}

      {/* --- step 2: month --------------------------------------------------- */}
      {step === 1 ? (
        <div className="flex flex-col gap-s-3" data-testid="birthday-step-month">
          <p className="text-meta text-ink-3">{year} 年</p>
          <div className="grid grid-cols-3 gap-s-2">
            {MONTH_LABELS.map((label, idx) => {
              const m = idx + 1;
              const isSelected = month === m;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setMonth(m);
                    setStep(2);
                  }}
                  data-testid={`birthday-month-${m}`}
                  data-selected={isSelected || undefined}
                  className={cn(
                    "tap-target h-11 rounded-sm text-body-sm font-semibold",
                    isSelected
                      ? "bg-ink-1 text-paper"
                      : "bg-paper-deep text-ink-1 hover:bg-black/5",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setStep(0)}
            className="tap-target press min-h-11 self-start rounded-sm px-s-3 text-body-sm text-ink-2 hover:bg-black/5"
          >
            重選年份
          </button>
        </div>
      ) : null}

      {/* --- step 3: day ----------------------------------------------------- */}
      {step === 2 ? (
        <div className="flex flex-col gap-s-3" data-testid="birthday-step-day">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(1)}
              aria-label="回到月份"
              data-testid="birthday-back-to-month"
              className="tap-target press flex items-center justify-center rounded-sm text-body-sm text-ink-2 hover:bg-black/5"
            >
              {year} 年 {month} 月
            </button>
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              disabled={year <= EARLIEST_YEAR && month === 1}
              aria-label="上個月"
              data-testid="birthday-month-prev"
              className="tap-target press flex items-center justify-center rounded-sm text-ink-2 hover:bg-black/5 disabled:opacity-30"
            >
              <ChevronLeft size={20} strokeWidth={1.5} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              disabled={year >= THIS_YEAR && month === 12}
              aria-label="下個月"
              data-testid="birthday-month-next"
              className="tap-target press flex items-center justify-center rounded-sm text-ink-2 hover:bg-black/5 disabled:opacity-30"
            >
              <ChevronRight size={20} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-s-1" aria-hidden="true">
            {WEEKDAY_SHORT.map((d) => (
              <div key={d} className="text-center text-micro text-ink-3">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-s-1" data-testid="birthday-day-grid">
            {Array.from({ length: leadingBlanks }, (_, i) => (
              <div key={`blank-${i}`} aria-hidden="true" />
            ))}
            {Array.from({ length: total }, (_, i) => i + 1).map((d) => {
              const isSelected = selected === d;
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => pickDay(d)}
                  data-testid={`birthday-day-${d}`}
                  data-selected={isSelected || undefined}
                  aria-pressed={isSelected}
                  aria-label={`${year} 年 ${month} 月 ${d} 日`}
                  className={cn(
                    "tap-target h-11 w-11 flex items-center justify-center rounded-xs",
                    "text-body-sm tabular-nums",
                    isSelected
                      ? "bg-you-strong font-semibold text-paper"
                      : "text-ink-1 hover:bg-black/5",
                  )}
                >
                  {d}
                </button>
              );
            })}
          </div>

          {isFeb29 ? <NonLeapChoiceField value={nonLeapChoice} onChange={onNonLeapChange} /> : null}
        </div>
      ) : null}

      {value ? (
        <button
          type="button"
          onClick={() => onChange(null)}
          data-testid="birthday-clear"
          className="tap-target press min-h-11 self-start rounded-sm px-s-3 text-meta text-ink-3 hover:bg-black/5"
        >
          清除生日
        </button>
      ) : null}
    </div>
  );

  function shiftMonth(delta: number) {
    let m = month + delta;
    let y = year;
    if (m < 1) {
      m = 12;
      y -= 1;
    } else if (m > 12) {
      m = 1;
      y += 1;
    }
    y = Math.min(THIS_YEAR, Math.max(EARLIEST_YEAR, y));
    setYear(y);
    setMonth(m);
  }
}

/**
 * The Feb 29 follow-up (profile spec §2.1 P3).
 *
 * Shown **only** when the picked day is Feb 29, and the copy says why it
 * exists. Without a choice the yearly roll has no answer for common years:
 * ADR-0050 §5 forbids inventing March 1, so the birthday would silently not
 * recur. The server enforces this too (422
 * `PROFILE_NON_LEAP_CHOICE_REQUIRED`) — the field is here so the person is
 * asked once, at the moment they are already choosing a date.
 */
function NonLeapChoiceField({
  value,
  onChange,
}: {
  value: NonLeapChoice | null;
  onChange?: (choice: NonLeapChoice) => void;
}) {
  return (
    <div
      className="flex flex-col gap-s-2 rounded-sm bg-paper-deep p-s-3"
      data-testid="birthday-non-leap"
    >
      <p className="text-body-sm font-semibold text-ink-1">平年當作哪一天？</p>
      <div className="flex gap-s-2">
        {NON_LEAP_CHOICES.map((choice) => {
          const isSelected = value?.month === choice.month && value?.day === choice.day;
          const label = nonLeapLabel(choice);
          return (
            <button
              key={label}
              type="button"
              onClick={() => onChange?.(choice)}
              data-testid={`birthday-non-leap-${choice.month}-${choice.day}`}
              data-selected={isSelected || undefined}
              aria-pressed={isSelected}
              className={cn(
                "tap-target h-11 flex-1 rounded-sm text-body-sm font-semibold",
                isSelected
                  ? "bg-ink-1 text-paper"
                  : "bg-paper-elevated text-ink-1 hover:bg-black/5",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      <p className="text-meta text-ink-3">
        只在平年（2027、2028…）生效。閏年照常是 2 月 29 日。
      </p>
    </div>
  );
}
